import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";

// Type definitions
interface MembershipDetails {
  id: string;
  [key: string]: unknown;
}

interface Payment {
  id: string;
  [key: string]: unknown;
}

interface Invoice {
  id: string;
  [key: string]: unknown;
}

interface Membership {
  id: string;
  [key: string]: unknown;
}

interface ListState {
  searchTerm: string;
  currentPage: number;
  itemsPerPage: number;
  monthFilter: string;
  statusFilter: string;
}

export interface MembershipState {
  membershipDetails: MembershipDetails | null;
  paymentsByMember: Payment[];
  memberInvoices: Invoice[];
  membershipsList: Membership[];
  listState: ListState;
}

const today = new Date();
const defaultMonth = `${today.getFullYear()}-${String(
  today.getMonth() + 1
).padStart(2, "0")}`;

const initialState: MembershipState = {
  membershipDetails: null,
  paymentsByMember: [],
  memberInvoices: [],
  membershipsList: [],
  listState: {
    searchTerm: "",
    currentPage: 1,
    itemsPerPage: 5,
    monthFilter: defaultMonth,
    statusFilter: "Not Generated",
  },
};

const membershipSlice = createSlice({
  name: "membership",
  initialState,
  reducers: {
    setMembership: (state, action) => {
      state.membershipDetails = action.payload;
    },
    setPaymentsByMember: (state, action) => {
      state.paymentsByMember = action.payload;
    },
    setMemberInvoices: (state, action) => {
      state.memberInvoices = action.payload;
    },
    setMembershipsList: (state, action) => {
      state.membershipsList = action.payload;
    },
    setListState: (state, action) => {
      state.listState = { ...state.listState, ...action.payload };
    },
  },
});

export const {
  setMembership,
  setPaymentsByMember,
  setMemberInvoices,
  setMembershipsList,
  setListState,
} = membershipSlice.actions;

// Optional-chain on `state.membership` so a missing Redux Provider in
// test render trees fails gracefully (selector returns undefined)
// instead of crashing the whole render with
// "Cannot read properties of undefined (reading 'membershipDetails')".
// In production `state.membership` always exists; the `?.` is a no-op.
export const selectMembershipDetails = (state: RootState) =>
  state.membership?.membershipDetails;

export const selectPaymentsByMember = (state: RootState) =>
  state.membership?.paymentsByMember;

export const selectMemberInvoices = (state: RootState) =>
  state.membership?.memberInvoices;

export const selectMembershipsList = (state: RootState) =>
  state.membership?.membershipsList;

export const selectListState = (state: RootState) => state.membership?.listState;

export default membershipSlice.reducer;
